import * as React from 'react'

import { Box, Button, OptionSelector, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InlineTooltip from 'modules/core/components/InlineTooltip'
import Popover from 'modules/core/components/Popover'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Complaint from 'modules/complaints/store/models/Complaint'
import { STATUS } from 'modules/complaints/store/models/Complaint'

interface Props {
  complaint: Complaint
}

const ComplaintIdAndActions = ({ complaint }: Props) => {
  const [open, toggle] = useToggle(false)

  const { performAction: updateStatus } = useAsyncAction(
    async (option: { name: string; value: string }) => {
      toggle(false)()

      const value = await complaint.rpc('update_status', {
        complaint_id: complaint.id,
        status: option.value,
      })
      complaint.set(value)
    }
  )

  const date = React.useMemo<string>(() => {
    if (!complaint.updatedAt) return '- '

    return i18n.t('complaints.overview.updated', {
      date: stringToDate(complaint.updatedAt as any, 'backend_datetime').fromNow(),
    })
  }, [complaint.updatedAt])

  const getOptions = () => {
    return [
      {
        name: i18n.t('complaints.status.in_progress'),
        value: STATUS.Acknowledged,
        disabled: complaint.status !== STATUS.Created,
      },
      {
        name: i18n.t('complaints.status.processed'),
        value: STATUS.Completed,
        disabled: complaint.status !== STATUS.Acknowledged,
      },
      {
        name: i18n.t('complaints.status.discarded'),
        value: STATUS.Discarded,
        disabled: complaint.status !== STATUS.Acknowledged,
      },
    ]
  }

  const complaintNumber = i18n.t('complaints.overview.complaint_number', {
    identifier: complaint.identifier,
  })

  return (
    <Box gap="s8" padding="s16">
      <Text.Normal size="300" color="grey1000" weight="medium">
        {complaintNumber}
      </Text.Normal>
      <Text.Normal size="200" color="grey1000">
        {date}
      </Text.Normal>
      <Box flexDirection="row" gap="s8" justifyContent="flexStart">
        <Popover
          attachment="top center"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button onClick={toggle(true)}>{i18n.t('complaints.overview.change_status')}</Button>
          <InlineTooltip>
            <OptionSelector
              selectedValue={complaint.status}
              items={getOptions()}
              onSelect={(option) =>
                updateStatus({ name: option.name, value: option.value as string })
              }
              hideSearch
            />
          </InlineTooltip>
        </Popover>
      </Box>
    </Box>
  )
}

export default observer(ComplaintIdAndActions)
